import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { api } from '../../api/client'
import type { CompetitorReport, CompetitorReportDetail, CompetitorReportSummary } from '../../api/types'
import { formatRelative } from '../../lib/format'
import { useMeta } from '../../hooks/useMeta'
import { CompetitorTable, SOURCE_LABELS } from './CompetitorTable'
import { QueryCosts } from './QueryCosts'
import { ReportProgress } from './ReportProgress'

function Sources({ report }: { report: CompetitorReport }) {
	if (report.method === 'keywords') return <>App Store searches for {report.keywords.join(', ')}</>
	return (
		<>
			{report.providers
				.map((p) => `${SOURCE_LABELS[p.name]}${p.status === 'failed' ? ` (failed: ${p.error})` : ''}`)
				.join(' and ')}
		</>
	)
}

/** Admin-only: competitors found by Claude and ChatGPT, reused for a week unless run again. */
export function CompetitorsPanel({ appId }: { appId: number }) {
	const client = useQueryClient()
	const meta = useMeta()
	const [storefront, setStorefront] = useState('us')
	const [selected, setSelected] = useState<string>()
	const list = useQuery({
		queryKey: ['competitor-reports', appId],
		queryFn: () => api.get<CompetitorReportSummary[]>(`/api/admin/apps/${appId}/competitor-reports`),
	})
	const reportId = selected ?? list.data?.[0]?.id
	const detail = useQuery({
		queryKey: ['competitor-report', reportId],
		queryFn: () => api.get<CompetitorReportDetail>(`/api/admin/competitor-reports/${reportId}`),
		enabled: reportId !== undefined,
		refetchInterval: (q) => (q.state.data?.status === 'pending' || q.state.data?.status === 'running' ? 2000 : false),
	})
	const start = useMutation({
		mutationFn: (refresh: boolean) =>
			api.post<CompetitorReportSummary>(`/api/admin/apps/${appId}/competitor-reports`, { storefront, refresh }),
		onSuccess: (r) => {
			setSelected(r.id)
			void client.invalidateQueries({ queryKey: ['competitor-reports', appId] })
		},
	})
	const d = detail.data
	const busy = start.isPending || d?.status === 'pending' || d?.status === 'running'

	return (
		<section className="card stack">
			<div className="row" style={{ justifyContent: 'space-between' }}>
				<h2 style={{ margin: 0 }}>Competitors</h2>
				<div className="row">
					<select aria-label="Competitor storefront" value={storefront} onChange={(e) => setStorefront(e.target.value)}>
						{(meta.data?.storefronts ?? [{ code: 'us', name: 'United States' }]).map((s) => (
							<option key={s.code} value={s.code}>
								{s.code.toUpperCase()} · {s.name}
							</option>
						))}
					</select>
					<button type="button" className="primary" disabled={busy} onClick={() => start.mutate(false)}>
						Find competitors
					</button>
					{d?.status === 'done' && (
						<button type="button" disabled={busy} onClick={() => start.mutate(true)}>
							Run again
						</button>
					)}
				</div>
			</div>
			{start.isError && (
				<div className="empty">
					{start.error.message === 'llm_unavailable'
						? 'No Claude or OpenAI API key is configured on the server.'
						: `Couldn’t start a report: ${start.error.message}`}
				</div>
			)}
			{!reportId ? (
				<div className="empty">
					{list.isPending
						? 'Loading…'
						: 'No report yet. Claude and ChatGPT are asked, then checked against the App Store.'}
				</div>
			) : !d ? (
				<div className="empty">Loading…</div>
			) : start.isPending ? (
				<div className="empty">Starting a report…</div>
			) : busy ? (
				<ReportProgress report={d} />
			) : d.status === 'failed' ? (
				<div className="empty">The report failed: {d.error}</div>
			) : d.report ? (
				<>
					<p className="muted small" style={{ margin: 0 }}>
						{d.report.storefront.toUpperCase()} · found by <Sources report={d.report} /> ·{' '}
						{d.cached ? 'reused from a report ' : 'generated '}
						{formatRelative(d.report.generatedAt)}
					</p>
					<QueryCosts report={d.report} cached={d.cached} />
					<CompetitorTable report={d.report} />
					{d.report.unresolved.length > 0 && (
						<p className="muted small" style={{ margin: 0 }}>
							Not found on the App Store: {d.report.unresolved.map((u) => u.name).join(', ')}
						</p>
					)}
				</>
			) : null}
		</section>
	)
}
